/*
  Grouped level filter (Workshop PLP), v17.

  Owns the "Pick your level" drawer end to end. The visible rows - ALL LEVELS, BEGINNER,
  INTERMEDIATE, ADVANCED - are deliberately unnamed controls; the hidden real value
  checkboxes are what reaches URLs and requests, so links always describe plain levels
  while shoppers pick ranges.

  Behaviour:
    - Fresh load: nothing ticked.
    - ALL LEVELS toggles: when nothing or some ranges are selected, it selects all ranges;
      when all ranges are already selected, it deselects all.
    - Individual ranges can be clicked independently and combine by union.
    - When all three ranges are individually selected, ALL LEVELS auto-checks.

  This script performs its own section fetch rather than handing off to facets.js: that
  serializer emits empty price params and races our checkbox writes. Group-row events never
  reach it because they are stopped during the document's capture phase.
*/

if (!window.levelGroupFilterReady) {
  window.levelGroupFilterReady = true;

  const GROUP_SELECTOR = 'input[data-level-group]';
  const VALUE_SELECTOR = 'input[data-level-value]';
  const LIST_SELECTOR = '.mobile-facets__list';
  const FORM_SELECTOR = '#FacetFiltersFormMobile';
  const GRID_ID = 'ProductGridContainer';
  const SUBMIT_DELAY = 250;

  let currentKeys = [];
  let submitTimer = null;
  let activeRequest = null;
  const watchedFilters = new WeakSet();

  const keyOf = (group) => group.dataset.levelGroup || '';

  const levelList = (keys) => {
    const levels = new Set();
    keys.forEach((key) =>
      String(key)
        .split('.')
        .filter(Boolean)
        .forEach((level) => levels.add(level)),
    );
    return Array.from(levels);
  };

  const enabledRangeInputs = (list) =>
    Array.from(list.querySelectorAll(GROUP_SELECTOR)).filter((group) => keyOf(group) !== '' && !group.disabled);

  const uniqueKeys = (keys) => Array.from(new Set(keys));

  const selectedKeysFromList = (list) => {
    const selectedGroups = Array.from(list.querySelectorAll(GROUP_SELECTOR))
      .filter((group) => keyOf(group) !== '' && group.checked)
      .map(keyOf);
    if (selectedGroups.length > 0) return selectedGroups;

    return Array.from(list.querySelectorAll(`${VALUE_SELECTOR}:checked`)).map((value) => value.dataset.levelValue);
  };

  const toggleKey = (keys, key) => (keys.includes(key) ? keys.filter((existing) => existing !== key) : [...keys, key]);

  const activeList = () => document.querySelector(`${FORM_SELECTOR} ${LIST_SELECTOR}`);

  function paint(list, keys) {
    if (!list) return;
    const ranges = uniqueKeys(keys.filter(Boolean));
    const rangesOnOffer = enabledRangeInputs(list);
    const allPicked = rangesOnOffer.length > 0 && rangesOnOffer.every((group) => ranges.includes(keyOf(group)));
    const levels = new Set(levelList(ranges));

    list.querySelectorAll(GROUP_SELECTOR).forEach((group) => {
      const picked = keyOf(group) === '' ? allPicked : ranges.includes(keyOf(group));
      group.checked = picked;
      const label = group.closest('label');
      if (label) label.classList.toggle('active', picked);
    });

    list.querySelectorAll(VALUE_SELECTOR).forEach((value) => {
      value.checked = levels.has(value.dataset.levelValue);
    });
  }

  function renderedMatches(list, keys) {
    const ranges = uniqueKeys(keys.filter(Boolean));
    const rangesOnOffer = enabledRangeInputs(list);
    const expectedAll = rangesOnOffer.length > 0 && rangesOnOffer.every((group) => ranges.includes(keyOf(group)));
    const levels = new Set(levelList(ranges));

    const groupsAgree = Array.from(list.querySelectorAll(GROUP_SELECTOR)).every(
      (group) => group.checked === (keyOf(group) === '' ? expectedAll : ranges.includes(keyOf(group))),
    );
    if (!groupsAgree) return false;

    return Array.from(list.querySelectorAll(VALUE_SELECTOR)).every(
      (value) => value.checked === levels.has(value.dataset.levelValue),
    );
  }

  function createSearchParams(form, keys) {
    const levelNames = new Set(
      Array.from(form.querySelectorAll(VALUE_SELECTOR))
        .map((value) => value.name)
        .filter(Boolean),
    );

    const params = new URLSearchParams();
    Array.from(new FormData(form).entries()).forEach(([name, value]) => {
      if (!name || levelNames.has(name) || String(value) === '') return;
      params.append(name, value);
    });

    const levels = levelList(keys.filter(Boolean));
    form.querySelectorAll(VALUE_SELECTOR).forEach((value) => {
      if (levels.includes(value.dataset.levelValue)) params.append(value.name, value.value);
    });

    return params.toString();
  }

  function applyRender(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const gridSource = parsed.getElementById(GRID_ID);
    const gridTarget = document.getElementById(GRID_ID);
    if (gridSource && gridTarget) {
      gridTarget.innerHTML = gridSource.innerHTML;
      gridTarget.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
    }

    const countSource = parsed.getElementById('ProductCount');
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const target = document.getElementById(id);
      if (countSource && target) {
        target.innerHTML = countSource.innerHTML;
        target.classList.remove('loading');
      }
    });

    parsed.querySelectorAll('.js-filter').forEach((source) => {
      if (source.querySelector(GROUP_SELECTOR)) return;
      const target = source.id ? document.getElementById(source.id) : null;
      if (target) target.innerHTML = source.innerHTML;
    });

    ['.active-facets-mobile', '.active-facets-desktop', '.mobile-facets__open', '.mobile-facets__count'].forEach(
      (selector) => {
        const source = parsed.querySelector(selector);
        const target = document.querySelector(selector);
        if (source && target) target.innerHTML = source.innerHTML;
      },
    );
  }

  function setLoading(on) {
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.toggle('hidden', !on));

    const collection = document.querySelector(`#${GRID_ID} .collection`);
    if (collection) collection.classList.toggle('loading', on);

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const container = document.getElementById(id);
      if (container) container.classList.toggle('loading', on);
    });
  }

  function fetchSection() {
    const list = activeList();
    const form = document.querySelector(FORM_SELECTOR);
    const grid = document.getElementById('product-grid');
    if (!list || !form || !(grid instanceof HTMLElement)) return;

    const params = createSearchParams(form, currentKeys);

    setLoading(true);
    if (activeRequest) activeRequest.abort();
    activeRequest = new AbortController();

    const url = `${window.location.pathname}?section_id=${grid.dataset.id}&${params}`;

    fetch(url, { signal: activeRequest.signal })
      .then((response) => response.text())
      .then((html) => {
        applyRender(html);
        paint(list, currentKeys);

        history.pushState({ searchParams: params }, '', `${window.location.pathname}${params ? `?${params}` : ''}`);
        if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.updateResetButtonsState) {
          FacetFiltersForm.updateResetButtonsState();
        }
        setLoading(false);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        console.error('[level-group-filter] fetch failed, navigating instead', error);
        window.location.search = params;
      });
  }

  function scheduleFetch() {
    clearTimeout(submitTimer);
    submitTimer = setTimeout(fetchSection, SUBMIT_DELAY);
  }

  function watch(list) {
    const filter = list && list.closest('.js-filter');
    if (!filter || watchedFilters.has(filter)) return;
    watchedFilters.add(filter);

    new MutationObserver(() => {
      const rendered = filter.querySelector(LIST_SELECTOR);
      if (!rendered) return;
      if (renderedMatches(rendered, currentKeys)) return;

      currentKeys = uniqueKeys(selectedKeysFromList(rendered));
      paint(rendered, currentKeys);
    }).observe(filter, { childList: true, subtree: true });
  }

  function nextState(list, clickedKey) {
    if (clickedKey === '') {
      const allRangeKeys = enabledRangeInputs(list).map(keyOf);
      const allAlreadyPicked = allRangeKeys.length > 0 && allRangeKeys.every((key) => currentKeys.includes(key));
      return allAlreadyPicked ? [] : allRangeKeys;
    }
    return toggleKey(currentKeys, clickedKey);
  }

  function commit(list, keys) {
    currentKeys = uniqueKeys(keys.filter(Boolean));

    paint(list, currentKeys);
    watch(list);
    if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.updateResetButtonsState) {
      FacetFiltersForm.updateResetButtonsState();
    }
    scheduleFetch();
  }

  document.addEventListener(
    'input',
    (event) => {
      if (event.target.matches && event.target.matches(GROUP_SELECTOR)) event.stopPropagation();
    },
    true,
  );

  document.addEventListener(
    'change',
    (event) => {
      const group = event.target;
      if (!group.matches || !group.matches(GROUP_SELECTOR)) return;
      event.stopPropagation();

      const list = group.closest(LIST_SELECTOR);
      if (!list) return;

      commit(list, nextState(list, keyOf(group)));
    },
    true,
  );

  document.addEventListener('click', (event) => {
    const pill = event.target.closest && event.target.closest('[data-level-group-remove]');
    if (!pill) return;

    const removed = pill.dataset.levelGroupRemove;
    const list = activeList();
    if (!list) return;

    event.preventDefault();
    commit(
      list,
      currentKeys.filter((key) => key !== removed),
    );
  });

  function boot() {
    const list = activeList();
    if (!list) return;

    currentKeys = uniqueKeys(selectedKeysFromList(list));
    watch(list);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}
